import {ShoppingCart, CreditCard} from 'lucide-react';
import {useState} from 'react';

import {Badge} from './components/ui/badge';
import {Button} from './components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './components/ui/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './components/ui/sheet';
import {useToast} from './hooks/use-toast';
import {useCredentialsV2} from '../../hooks/hmac/useHmacV2';

interface Product {
  id: number;
  name: string;
  price: number;
  description: string;
  image: string;
  category: string;
  featured?: boolean;
}

interface CartItem extends Product {
  quantity: number;
}

const products: Product[] = [
  {
    id: 1,
    name: 'Premium Wireless Headphones',
    price: 299,
    description:
      'High-quality wireless headphones with noise cancellation and superior sound quality.',
    image:
      'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=300&fit=crop',
    category: 'Electronics',
    featured: true,
  },
  {
    id: 2,
    name: 'Smart Fitness Watch',
    price: 5,
    description:
      'Track your fitness goals with this advanced smartwatch featuring heart rate monitoring.',
    image:
      'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=300&fit=crop',
    category: 'Wearables',
  },
  {
    id: 3,
    name: 'Portable Bluetooth Speaker',
    price: 79,
    description:
      'Compact speaker with powerful sound and 12-hour battery life. Perfect for any occasion.',
    image:
      'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=400&h=300&fit=crop',
    category: 'Electronics',
  },
  {
    id: 4,
    name: 'Professional Camera Lens',
    price: 899,
    description:
      'High-performance 85mm lens perfect for portrait and professional photography.',
    image:
      'https://images.unsplash.com/photo-1606983340126-99ab4feaa64a?w=400&h=300&fit=crop',
    category: 'Photography',
    featured: true,
  },
  {
    id: 5,
    name: 'Ergonomic Office Chair',
    price: 101,
    description:
      'Comfortable office chair with lumbar support and adjustable height for long work sessions.',
    image:
      'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
    category: 'Furniture',
  },
  {
    id: 6,
    name: 'Mechanical Gaming Keyboard',
    price: 159,
    description:
      'RGB backlit mechanical keyboard with custom switches for the ultimate gaming experience.',
    image:
      'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400&h=300&fit=crop',
    category: 'Gaming',
  },
  {
    id: 7,
    name: 'SuperCustom Keyboard',
    price: 300000,
    description:
      'RGB backlit mechanical keyboard with custom switches for the ultimate gaming experience.',
    image:
      'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400&h=300&fit=crop',
    category: 'Luxury',
  },
  {
    id: 8,
    name: 'SuperCustom Keyboard',
    price: 300000,
    description:
      'RGB backlit mechanical keyboard with custom switches for the ultimate gaming experience.',
    image:
      'https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=400&h=300&fit=crop',
    category: 'Luxury',
  },
];

const ShopPage = () => {
  const {getCredentials} = useCredentialsV2();
  const _storedCreds = getCredentials();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const {toast} = useToast();

  const addToCart = (product: Product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id ? {...item, quantity: item.quantity + 1} : item
        );
      }
      return [...prevCart, {...product, quantity: 1}];
    });

    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`,
    });
  };

  const removeFromCart = (productId: number) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
    toast({
      title: 'Removed from cart',
      description: 'Item has been removed from your cart.',
    });
  };

  const updateQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity === 0) {
      removeFromCart(productId);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId ? {...item, quantity: newQuantity} : item
      )
    );
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast({
        title: 'Cart is empty',
        description: 'Please add some items to your cart before checking out.',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);

    // authorize and create a hosted checkout session
    try {
      // const requestBody: CheckoutRequest = {
      //   terminalId: storedCreds?.terminalId || '',
      //   requestId: `checkout-${Date.now()}`,
      //   amount: getTotalPrice(),
      //   currency: 'KES',
      //   successRedirectUrl: `${window.location.origin}/shop/success`,
      //   errorRedirectUrl: `${window.location.origin}/shop/error`,
      //   webhookUrl: 'https://www.cashia.com/webhooks/cashia', // Example webhook URL
      // };
      // // Validate authentication parameters
      // validateAuthParamsV2(
      //   storedCreds?.keyId || '',
      //   storedCreds?.secretKey || ''
      // );
      // // Generate headers using the reusable utility
      // const headers = generateCashiaHeadersV2({
      //   secret: storedCreds?.secretKey || '',
      //   keyId: storedCreds?.keyId || '',
      //   requestBody,
      //   method: 'POST',
      // });
      // const checkoutResponse = await checkout(requestBody, headers);
      // if (checkoutResponse?.url) {
      //   window.location.href = checkoutResponse.url;
      // } else {
      //   toast({
      //     title: 'Checkout failed',
      //     description:
      //       'Authentication credentials (keyId, secret and terminalId) are required',
      //     variant: 'destructive',
      //   });
      //   throw new Error('Failed to create checkout session');
      // }
    } catch (error) {
      setIsProcessing(false);
      toast({
        title: 'Checkout failed',
        description:
          'There was an error processing your payment. Please try again later.',
        variant: 'destructive',
      });
      return;
    }
    setIsProcessing(false);
    setCart([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">TechStore</h1>
              <p className="text-slate-600">
                Premium tech products for modern life
              </p>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative p-3">
                  <ShoppingCart className="h-5 w-5" />
                  {getTotalItems() > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-primary text-xs">
                      {getTotalItems()}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Shopping Cart ({getTotalItems()} items)
                  </SheetTitle>
                  <SheetDescription>
                    Review your items and proceed to checkout
                  </SheetDescription>
                </SheetHeader>

                <div className="mt-6">
                  {cart.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Your cart is empty. Start shopping to add items!</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-4 mb-6">
                        {cart.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-3 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded-md"
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-800 text-sm">
                                {item.name}
                              </h3>
                              <p className="text-slate-600 text-xs">
                                KES {item.price} each
                              </p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity - 1)
                                }>
                                -
                              </Button>
                              <span className="w-6 text-center text-xs font-semibold">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() =>
                                  updateQuantity(item.id, item.quantity + 1)
                                }>
                                +
                              </Button>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-800 text-sm">
                                KES {(item.price * item.quantity).toFixed(2)}
                              </p>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => removeFromCart(item.id)}>
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-4">
                        <div className="flex justify-between items-center mb-4 text-lg font-bold">
                          <span>Total:</span>
                          <span className="text-xl text-slate-800">
                            KES {getTotalPrice().toFixed(2)}
                          </span>
                        </div>
                        <Button
                          onClick={handleCheckout}
                          disabled={isProcessing}
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 transition-all duration-300 py-2">
                          {isProcessing ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4 mr-2" />
                              Proceed to Checkout
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Products Section */}
        <section className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              Featured Products
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Discover our carefully curated selection of premium tech products
              designed to enhance your digital lifestyle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card
                key={product.id}
                className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {product.featured && (
                    <Badge className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500">
                      Featured
                    </Badge>
                  )}
                  <Badge variant="secondary" className="absolute top-3 right-3">
                    {product.category}
                  </Badge>
                </div>

                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-slate-800">
                    {product.name}
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    {product.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-slate-800">
                      KES {product.price}
                    </span>
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    onClick={() => addToCart(product)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all duration-300">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ShopPage;
